import React, { useState, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ImageUploadField from '../../components/admin/ImageUploadField';
import { getAuthor, updateAuthor } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';

const textFields = [
  { name: 'name', label: 'Full Name' },
  { name: 'title', label: 'Title / Role' },
  { name: 'email', label: 'Email' },
  { name: 'phone', label: 'Phone' },
  { name: 'whatsapp', label: 'WhatsApp Number (e.g. 919876543210)' },
  { name: 'facebook', label: 'Facebook URL' },
  { name: 'twitter', label: 'Twitter URL' },
  { name: 'instagram', label: 'Instagram URL' },
  { name: 'linkedin', label: 'LinkedIn URL' },
  { name: 'youtube', label: 'YouTube URL' },
];

export default function AdminAuthor() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAuthor().then((data) => setForm(data || {})).catch(() => setForm({}));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const setList = (k, i, v) => {
    const arr = [...(form[k] || [])];
    arr[i] = v;
    set(k, arr);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name || '', title: form.title || '', bio: form.bio || '',
        mission: form.mission || '', vision: form.vision || '', image: form.image || '',
        email: form.email || '', phone: form.phone || '', whatsapp: form.whatsapp || '',
        facebook: form.facebook || '', twitter: form.twitter || '', instagram: form.instagram || '',
        linkedin: form.linkedin || '', youtube: form.youtube || '',
        achievements: (form.achievements || []).filter((x) => x && x.trim()),
        timeline: form.timeline || [],
      };
      await updateAuthor(payload);
      toast({ title: 'Saved', description: 'Author profile updated successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-orange-500 animate-spin" /></div>;
  }

  const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none';
  const achievements = form.achievements || [];

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">Author Profile</h1>
      <p className="text-slate-500 text-sm mb-6">Manage your bio, mission, contact & social links</p>

      <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 max-w-3xl">
        <ImageUploadField value={form.image} onChange={(v) => set('image', v)} label="Profile Photo" />

        <div className="grid sm:grid-cols-2 gap-4">
          {textFields.map((f) => (
            <div key={f.name}>
              <label className="block text-sm font-medium text-slate-700 mb-2">{f.label}</label>
              <input value={form[f.name] || ''} onChange={(e) => set(f.name, e.target.value)} className={inputCls} data-testid={`author-${f.name}-input`} />
            </div>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Biography</label>
          <textarea rows={4} value={form.bio || ''} onChange={(e) => set('bio', e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Mission</label>
          <textarea rows={3} value={form.mission || ''} onChange={(e) => set('mission', e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Vision</label>
          <textarea rows={3} value={form.vision || ''} onChange={(e) => set('vision', e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Achievements</label>
          <div className="space-y-2">
            {achievements.map((a, i) => (
              <div key={i} className="flex gap-2">
                <input value={a} onChange={(e) => setList('achievements', i, e.target.value)} className={inputCls} />
                <button type="button" onClick={() => set('achievements', achievements.filter((_, idx) => idx !== i))} className="px-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">×</button>
              </div>
            ))}
            <button type="button" onClick={() => set('achievements', [...achievements, ''])} className="text-sm text-orange-600 font-medium">+ Add achievement</button>
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="save-author-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Profile
        </Button>
      </form>
    </div>
  );
}
